// src/services/contributionTableClient.js

/**
 * Shared client-side helpers for the GLOBAL `contributionTables` collection
 * published by the platform app (BL-062). Extracted from sssContributionTable.js
 * so the SSS (BL-063), PhilHealth (BL-073) and Pag-IBIG (BL-074) employee-setup
 * suggestion consumers all use ONE definition of version resolution + the
 * monthly-salary basis — the three chips can never disagree for daily-rate
 * employees.
 *
 * Read-only, suggestion-only, table-agnostic: version resolution by effective
 * date + the salary→monthly derivation. The per-table lookup/compute (SSS bracket
 * vs PhilHealth formula) lives in each table's own service.
 */

import { todayManilaYMD } from '../utils/periodDate';

/**
 * Round to 2 decimals (half-up), absorbing binary-float drift.
 * @param {number} n - the value to round.
 * @returns {number} n rounded to two decimal places.
 */
export function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * The in-force version for a given day: the greatest effectiveDate ≤ today.
 * Future-dated versions (effectiveDate > today) have resolved for no one yet
 * and are ignored. Returns null when none is in force.
 *
 * @param {Array<Object>} versions - version docs (each with an `effectiveDate`)
 * @param {string} [todayYMD] - 'YYYY-MM-DD' in Asia/Manila (defaults to today)
 * @returns {?Object} the in-force version, or null
 */
export function resolveInForceVersion(versions, todayYMD = todayManilaYMD()) {
  if (!Array.isArray(versions)) return null;
  let best = null;
  for (const v of versions) {
    const eff = v && v.effectiveDate;
    if (typeof eff !== 'string') continue;
    if (eff <= todayYMD && (best === null || eff > best.effectiveDate)) {
      best = v;
    }
  }
  return best;
}

/**
 * The monthly compensation a contribution should be looked up/computed against,
 * given an employee's compensation. Monthly-salary employees use `monthlySalary`;
 * daily-salary employees derive a monthly-equivalent from the company rate
 * factor (`daily × dailyRateFactor ÷ 12`). Returns null when the relevant salary
 * (or, for daily, the factor) is missing or non-positive.
 *
 * @param {Object} args
 * @param {string} args.salaryType - 'monthly' | 'daily'
 * @param {number|string} [args.monthlySalary]
 * @param {number|string} [args.dailySalary]
 * @param {number|string} [args.dailyRateFactor] - settings/payrollRates factor
 * @returns {?number} monthly compensation in pesos, or null
 */
export function monthlyGrossForEmployee({
  salaryType,
  monthlySalary,
  dailySalary,
  dailyRateFactor,
}) {
  if (salaryType === 'monthly') {
    const m = parseFloat(monthlySalary);
    return isFinite(m) && m > 0 ? m : null;
  }
  // Daily (the wizard default): derive the monthly-equivalent from the
  // company's own monthly→daily rate factor.
  const d = parseFloat(dailySalary);
  const factor = parseFloat(dailyRateFactor);
  if (!isFinite(d) || d <= 0 || !isFinite(factor) || factor <= 0) return null;
  return round2((d * factor) / 12);
}
